import { map, size, last, now } from 'lodash';

import { areEqual, clone } from 'gear';

import { Point, Bounds, Cubic, Curve, Boundary,
         Shape, AbsoluteAnchor, Polyregion, Region } from 'cdl';

import { Tool, padScaling } from '../view';

const { distance2 } = Point;

export const FreeDrawTool = Tool.extend({ typeName: 'FreeDrawTool',

  Properties: {
    pen       : { type: 'Pen'    , def: Tool.defaultPen(), onChange() { this.redraw(); } },
    brush     : { type: 'Brush'  , def: Tool.defaultBrush(), onChange() { this.redraw(); } },
    detectCorners: { type: 'boolean', def: true }
  },

  type: 'Final',
  toolbarName: 'freeDrawTool',

  Modes: { 0: 'Free', 1: 'Rectangle', 2: 'Circle' },

  initTool( view, config = {} ) {
    this._bind('switchCorners');

    this._closedConfig = config.closed !== undefined ? config.closed : false;
    this.flushShape();
    this._mode = 0; // 'Free'
    this.closeDistance = 0.1  * 96;
    this.flushCurrentPath();
  },

  flushShape() {
    this._closed = this._closedConfig;
    this._paths = [];
    this._redoPaths = [];
  },

  switchCorners() {
    this._detectCorners = ! this._detectCorners;
    // this.updateActions();
  },

  draw({ view, context, transform }) {

    // Draw semitransparent mask
    //context.fillStyle = 'rgba(255,255,255,0.75)';
    //fillContext(context);

    this._pen.setupContext(context,transform);

    const brush = this._brush, solidBrush = ( brush._type === 'Brush' );

    if( solidBrush ) {
      context.fillStyle = brush._color.rgba;
    } else {
      // Draw temporary semitransparent mask
      // Brush is applied to the Polyregion in commitChanges$()
      // This way all brush types are supported
      context.fillStyle = 'rgba(255,255,255,0.5)';
    }

    context.beginPath();

    const paths = clone(this._paths);

    if( this.inMode('Free') ) {
      const currentPoints = clone(this._points);
      if( this._nextPoint ) {
        currentPoints.push( this._nextPoint );
      }
      if( currentPoints.length > 1 ) {
        paths.push( currentPoints );
      }
    }
    else if( this._drawing ) {
      const stampPath = this.stampPath();
      if( stampPath ) {
        paths.push( stampPath );
      }
    }

    let lastIsClosed = false;

    for( let k = 0, kEnd = paths.length; k<kEnd ; ++k ) {

      const paths_k = paths[k];

      const isCurve = paths_k instanceof Curve;

      const closed = isCurve || this._closed || paths_k[0].isNear( last(paths_k), this.closeDistance/transform.scaleFactor()* padScaling );

      if( !closed && lastIsClosed ) {
        context.stroke();
        if( solidBrush ) {
          context.fill('evenodd');
        }
        context.beginPath();
      }

      if( paths_k instanceof Curve ) {
        paths_k.draw(context,transform);
      }
      else {
        const path = map( paths_k, p => transform.apply(p) );
        const length = path.length - 1;

        path.unshift(path[0]);
        path.push( path[path.length - 1] );

        if( length <= 0 ) {
          return;
        }
        for(let n = 0; n < length; n ++) {
          const p1 = path[n    ],
                p2 = path[n + 1],
                p3 = path[n + 2],
                p4 = path[n + 3];

          if(n === 0) {
            context.moveTo(p2.x, p2.y);
          }
          if( ! areEqual(p2,p3) ) {
            context.bezierCurveTo(
              p2.x + ( p3.x - p1.x) / 6,  p2.y + ( p3.y - p1.y) / 6,
              p3.x + ( p2.x - p4.x) / 6,  p3.y + ( p2.y - p4.y) / 6,
              p3.x                     ,  p3.y
            );
          }
        }

        if( closed ) {
          context.closePath();
        }
        else {
          context.stroke();
          context.beginPath();
        }
        lastIsClosed = closed;
      }
    }
    if( lastIsClosed ) {
      context.stroke();
      if( solidBrush ) {
        context.fill('evenodd');
      }
      context.beginPath();
    }

  },
  switchMode() {
    this._mode++;
    if( this._mode >= size(this.Modes) ) {
      this._mode = 0;
    }
    this.redraw();
  },
  inMode( m) {
    return this.Modes[this._mode] === m ;
  },
  stampBounds() {
    return Bounds.ofPoints([this._pointDown,this._pointTo]);
  },
  stampPath() {
    if( areEqual(this._pointDown, this._pointTo )) {
      return null;
    }
    const bounds = this.stampBounds();
    if( this.inMode('Rectangle') ) {
      return Boundary.createRectangle(bounds);
    }
    else if( this.inMode('Circle') ) {
      return Boundary.createEllipse(bounds);
    }
    return null;
  },

  on_pointerdown({ event, isPrimary, which, docPoint }) {
    if( isPrimary === false ) {
      return; // ignore multitouch
    }
    const t = now();
    if( t - this._lastTime < 500 ) {
      this.commit$();
      return;
    }

    event.stopPropagation();

    this._lastTime = t;

    const leftClick = which === 1 ;
    if( leftClick ) {
      this.toolhelp('Hold and move to draw path',
                    'Release near initial point to close');
      this._drawing = true;
      const np = docPoint;

      if( this.inMode('Free') ) {

        this._points.push(np);
        this._lastTime = t;

        this._pointsR.push(np);
        this._lastTimeR = t;
      }
      else {
        this._pointDown = np;
        this._pointTo   = np;
      }

      this.redraw();
      this._redoPaths = [];
    }
  },

  on_pointermove({ event, view, isPrimary, cmdKey }) {
    if( ! isPrimary ) {
      return; // ignore multitouch
    }

    const ctrl = this._ctrl = cmdKey;
    if( this._drawing ) {
      const points = this._points, pointsR = this._pointsR;

      const t = now();
      const { docPoint } = event;

      if( this.inMode('Free') ) {

        if( this._detectCorners ) {

          if( t - this._lastTimeR > 25 ) {

            let lp = last(pointsR);

            const deltaVL = view.viewDeltaToDoc(3);

            if( distance2( docPoint, lp ) > deltaVL*deltaVL ) {
              const lp3TimeR = this._lastTimeRPrevPrev;

              pointsR.push( docPoint );

              this._lastTimeRPrevPrev = this._lastTimeRPrev;
              this._lastTimeRPrev = this._lastTimeR;
              this._lastTimeR = t;

              const delta = view.viewDeltaToDoc(14), delta2 = delta*delta;

              const pointsR_length = pointsR.length;
              if( pointsR_length > 7 ) {

                const lp0 = pointsR[pointsR_length-1];
                const lp1 = pointsR[pointsR_length-2];
                const lp2 = pointsR[pointsR_length-3];
                const lp3 = pointsR[pointsR_length-4];
                const lp4 = pointsR[pointsR_length-5];
                const lp5 = pointsR[pointsR_length-6];
                const lp6 = pointsR[pointsR_length-7];

                const turnAngle1 = Point.turnAngle( lp2.minus(lp3) ,
                                                  lp3.minus(lp4) );

                const turnAngle2 = Point.turnAngle( lp0.minus(lp3).add( lp1.minus(lp3).add( lp2.minus(lp3) ) ) ,
                                                  lp3.minus(lp6).add( lp3.minus(lp5).add( lp3.minus(lp6) ) ) );

                if( Math.abs( turnAngle1 ) > 50 && Math.abs( turnAngle2 ) > 75 ) {
                  if( ! last(points).__corner__ && ( lp3TimeR < this._lastTime || distance2(last(points),lp3) < delta2 ) ) {
                    points.pop();
                  }
                  if( points.length > 0 && ! last(points).__corner__ && ( lp3TimeR < this._lastTimePrev || distance2(last(points),lp3) < delta2 ) ) {
                    points.pop();
                  }
                  while( points.length > 0 && ! last(points).__corner__ && distance2(last(points),lp3) < delta2 ) {
                    points.pop();
                  }

                  const lpd3 = pointsR[pointsR.length-4];
                  points.push( lpd3 );
                  points.push( lpd3 );
                  lpd3.__corner__ = true;
                }
              }
            }
          }
        }

        if( ctrl && points.length > 2 ) {
          const l = points.length;
          if( ! areEqual(points[l-1],points[l-2]) ) {
            points.push(points[l-1]);
            points[l-1].__corner__ = true;
          }
          this._nextPoint = docPoint;
          this.redraw();
        }
        else if( t - this._lastTime > 40 ) {
          const delta = view.viewDeltaToDoc(17);
          if( distance2( docPoint, last(points) ) > delta*delta ) {
            points.push( docPoint );
            this.redraw();
            this._lastTimePrev = this._lastTime;
            this._lastTime = t;
            this._nextPoint = null;
          }
        }
        if( this._lastTime !== t ) {
          this._nextPoint = docPoint;
          this.redraw();
        }
      }
      else {
        this._pointTo = docPoint;
        if( ctrl ) {
          const w = this._pointTo.x-this._pointDown.x;
          const h = this._pointTo.y-this._pointDown.y;
          if( w && h ) {
            const wa = Math.abs(w);
            const ha = Math.abs(h);
            if( wa > ha ) {
              this._pointTo.y = this._pointDown.y + h * wa/ha;
            }
            else if( ha > wa ) {
              this._pointTo.x = this._pointDown.x + w * ha/wa;
            }
          }
        }
        this.redraw();
      }
    }
  },

  on_pointerup( event ) {
    if( event.isPrimary === false ) {
      return; // ignore multitouch
    }
    if( ! this._drawing ) {
      return;
    }

    if( this.inMode('Free' ) ) {
      const np = this._nextPoint;
      if( np && ! np.isEqual( last(this._points) ) ) {
        this._points.push(np);
      }
      if( this._points.length > 2 ) {
        this._paths.push( this._points );
      }
    }
    else {
      const stampPath = this.stampPath();
      if( stampPath ) {
        this._paths.push( stampPath );
      }
    }
    this.flushCurrentPath();
    this.commitChanges$();
  },
  flushCurrentPath() {
    this.toolhelp('Click and hold to draw path');
    this._redoPaths = [];
    this._points = [];
    this._pointsR = [];
    this._nextPoint = null;
    this._drawing = false;
    this._ctrl = false;
  },

  catmull2beziers( points) {
    const path = points.slice(0);

    const length = path.length - 1;

    path.splice(0, 0, path[0]);
    path.push(path[path.length - 1]);

    const pieces = [];
    if( length <= 0 ) {
      return;
    }
    for(let n = 0; n < length; n ++) {
      const p1 = path[n    ],
            p2 = path[n + 1],
            p3 = path[n + 2],
            p4 = path[n + 3];

      if( ! areEqual(p2,p3) ) {
        const q1 = p2.clone(),
              q2 = new Point( p2.x + (p3.x - p1.x) / 6,  p2.y + (p3.y - p1.y) / 6 ),
              q3 = new Point( p3.x + (p2.x - p4.x) / 6,  p3.y + (p2.y - p4.y) / 6 ),
              q4 = p3.clone();

        pieces.push( new Cubic(q1,q2,q3,q4) );
      }
    }
    return pieces;
  },
  undo() {
    if( ! this._drawing ) {
      if( this._paths.length > 0 ) {
        this._redoPaths.push(this._paths.pop());
      }
      this.redraw();
    }
  },
  redo() {
    if( ! this._drawing ) {
      if( this._redoPaths.length > 0 ) {
        this._paths.push( this._redoPaths.pop() );
      }
      this.redraw();
    }
  },
  _cancel$() {
    // Nothing to do...
  },
  _commit$() {
    return this.commitChanges$();
  },
  commitChanges$() {
    const view = this.view, paths = this._paths;
    if( paths.length > 0 ) {

      const pen = this._pen, brush = this._brush;

      const polyregions = [];
      const addPolyregion = function(regions,closed) {

        const polyregion = new Polyregion({ regions:regions, fillMode: 'Alternate', pen:pen });
        if( closed ) {
          polyregion.brush = brush;
        }
        polyregions.push(polyregion);
      };

      let regions = [];
      let lastWasClosed = false;
      for( let k = 0, kEnd = paths.length; k<kEnd; ++k ) {
        const paths_k = paths[k];
        const isCurve = paths_k instanceof Curve;

        let curve;

        // [TODO] We need to perform this check at the mousemove level, and not here.
        const closed = isCurve || this._closed || paths_k[0].isNear( last(paths_k), this.view.viewDeltaToDoc(this.closeDistance) * padScaling );

        if( closed !== lastWasClosed && regions.length > 0 ) {
          addPolyregion(regions,lastWasClosed);
          regions = [];
        }

        if( isCurve ) {
          curve = paths_k;
        }
        else {
          const pieces = this.catmull2beziers(paths_k);
          curve = Curve.fromNonTiedPieces(pieces,closed);
        }
        const region = new Region([curve]);
        regions.push( region );

        lastWasClosed = closed;
      }

      if( regions.length > 0 ) {
        addPolyregion(regions,lastWasClosed);
        regions = [];
      }

      const anchor = Bounds.ofThings(polyregions).center;

      view.add( Shape({ polyregions: polyregions,
                           anchor: AbsoluteAnchor(anchor),
                           pin: anchor.clone() }) );

      this.flushShape();
      view.keepSelection();
      return view.commit$();
    }
  }
});

Tool.FreeDraw = FreeDrawTool;



/** WEBPACK FOOTER **
 ** ./src/ui/tool/free-draw.js
 **/